import React, { useState } from 'react';
import { Home, Building2, Factory, CircuitBoard, AlertCircle, Zap, ChevronDown, ChevronUp, Lightbulb, Car } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { services } from '../mockData';

const iconMap = {
  Home,
  Building2,
  Factory,
  CircuitBoard,
  AlertCircle,
  Zap
};

const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const energyEfficiencySubServices = [
    {
      id: 'smart-home',
      title: 'Smart Home Integration & Automation',
      description: 'Complete smart home solutions including lighting control, thermostats, security systems, and voice integration.',
      icon: Lightbulb
    },
    {
      id: 'ev-charger',
      title: 'EV Charger Installations',
      description: 'Electric vehicle charging stations with power management systems for home and commercial properties.',
      icon: Car
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-br from-white via-gray-50 to-white relative overflow-hidden">
      {/* Playful background elements */}
      <div className="absolute top-20 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-6xl">⚡</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            What We Do Best
          </h2>
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="h-1 w-12 bg-gold rounded-full"></div>
            <div className="h-2 w-2 bg-gold rounded-full"></div>
            <div className="h-1 w-20 bg-gold rounded-full"></div>
            <div className="h-2 w-2 bg-gold rounded-full"></div>
            <div className="h-1 w-12 bg-gold rounded-full"></div>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            From cozy homes to big commercial projects—we've got the spark! ✨
          </p>
        </div>

        {/* Services Grid - Staggered and Playful */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isEven = index % 2 === 0;
            const isExpanded = expandedService === service.id;
            const isEnergyEfficiency = service.id === 6; // Energy Efficiency service
            
            return (
              <div key={service.id} className={isEven ? 'lg:translate-y-4' : ''}>
                <Card 
                  className={`group hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:border-gold/50 bg-white rounded-3xl overflow-hidden transform hover:-translate-y-4 ${isEnergyEfficiency ? 'cursor-pointer' : ''} ${isExpanded ? 'ring-2 ring-gold' : ''}`}
                  onClick={() => isEnergyEfficiency && toggleService(service.id)}
                  style={{
                    transitionDelay: `${index * 50}ms`
                  }}
                >
                  <CardHeader className="text-center p-8 relative">
                    {/* Decorative corner element */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gold/5 rounded-bl-full"></div>
                    
                    <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-gold/20 to-gold/5 mx-auto mb-6 group-hover:from-gold/30 group-hover:to-gold/10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 relative">
                      <IconComponent className="w-12 h-12 text-gold relative z-10" />
                      <div className="absolute inset-0 bg-gold/10 rounded-full blur-xl group-hover:blur-2xl transition-all"></div>
                    </div>
                    
                    <div className="flex items-center justify-center gap-2">
                      <CardTitle className="text-2xl mb-3 text-black group-hover:text-gold transition-colors font-bold">
                        {service.title}
                      </CardTitle>
                      {isEnergyEfficiency && (
                        <div className="mb-3">
                          {isExpanded ? (
                            <ChevronUp className="w-6 h-6 text-gold" />
                          ) : (
                            <ChevronDown className="w-6 h-6 text-gold animate-bounce" />
                          )}
                        </div>
                      )}
                    </div>
                    
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                </Card>

                {/* Sub-services for Energy Efficiency */}
                {isEnergyEfficiency && isExpanded && (
                  <div className="mt-6 space-y-4 animate-in slide-in-from-top">
                    {energyEfficiencySubServices.map((subService) => {
                      const SubIcon = subService.icon;
                      return (
                        <Card 
                          key={subService.id}
                          className="border-2 border-gold/30 bg-gradient-to-br from-gold/5 to-white hover:shadow-lg transition-all duration-300 rounded-2xl"
                        >
                          <CardHeader className="p-6">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                                <SubIcon className="w-6 h-6 text-gold" />
                              </div>
                              <div>
                                <CardTitle className="text-lg mb-2 text-black">
                                  {subService.title}
                                </CardTitle>
                                <CardDescription className="text-gray-600 text-sm">
                                  {subService.description}
                                </CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA - More Playful */}
        <div className="text-center mt-16 bg-gradient-to-r from-gold/10 via-gold/5 to-gold/10 rounded-3xl p-8">
          <p className="text-xl text-gray-700 mb-4 font-semibold">
            Don't see what you need? 🤔
          </p>
          <button 
            onClick={() => window.location.href = '/contact'}
            className="text-gold font-bold hover:text-black hover:bg-gold px-6 py-3 rounded-full transition-all duration-300 text-lg border-2 border-gold hover:scale-105 transform"
          >
            Let's talk about your project! →
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
